/**
 * Achievement tracking system for Surggies.
 *
 * Game systems call typed event hooks (onCatch, onTrainerDefeat, ...) and the
 * tracker decides which achievements unlock. Subscribers (e.g. the toast
 * component) are notified via onUnlock.
 *
 * Persisted state is a plain JSON-serializable object so it can be embedded
 * inside the save-system payload.
 */

export type AchievementId =
  | 'first_catch'
  | 'first_evolution'
  | 'first_trainer_win'
  | 'first_save'
  | 'caught_10'
  | 'caught_25'
  | 'caught_all'
  | 'defeated_10_trainers'
  | 'defeated_all_trainers'
  | 'reached_route_1'
  | 'reached_town'
  | 'reached_cave'
  | 'reached_legendary'
  | 'level_25'
  | 'level_50'
  | 'full_party'
  | 'no_faints_route_1'
  | 'speedrun_route_1'
  | 'million_dollar_team'
  // --- New achievements (batch 2) ------------------------------------------
  | 'map_explorer'
  | 'trainer_first_win'
  | 'caught_five'
  | 'plugin_master'
  | 'all_types'
  | 'battle_hundred'
  | 'rich'
  | 'speedrun';

export type Achievement = {
  id: AchievementId;
  name: string;
  description: string;
  /** Emoji or pixel-symbol char rendered in the toast / achievements panel. */
  icon: string;
  /** Hidden until unlocked — name/description show as "???" in the list. */
  hidden?: boolean;
};

/**
 * Total roster of Surggies species + named trainers used by accumulation goals.
 * Kept here (not in species.ts) so the tracker is self-contained and can be
 * tested without dragging in the full species module.
 */
const TOTAL_SPECIES = 12;
const TOTAL_TRAINERS = 8;

export const ACHIEVEMENTS: Record<AchievementId, Achievement> = {
  // --- First-time milestones (startup-themed) -------------------------------
  first_catch: {
    id: 'first_catch',
    name: 'Seed Round Closed',
    description: 'Recruit your first Surggie to the team.',
    icon: '🥚',
  },
  first_evolution: {
    id: 'first_evolution',
    name: 'Series A',
    description: 'Evolve a Surggie for the first time.',
    icon: '🦋',
  },
  first_trainer_win: {
    id: 'first_trainer_win',
    name: 'First Customer Win',
    description: 'Defeat a rival trainer in battle.',
    icon: '🏆',
  },
  first_save: {
    id: 'first_save',
    name: 'Incorporated',
    description: 'Save your progress for the first time.',
    icon: '💾',
  },

  // --- Collection ----------------------------------------------------------
  caught_10: {
    id: 'caught_10',
    name: 'Hiring Spree',
    description: 'Recruit 10 unique Surggies.',
    icon: '👥',
  },
  caught_25: {
    id: 'caught_25',
    name: 'Talent Acquisition',
    description: 'Recruit 25 Surggies total.',
    icon: '📈',
  },
  caught_all: {
    id: 'caught_all',
    name: 'Full Cap Table',
    description: 'Recruit every Surggie species.',
    icon: '🌟',
  },

  // --- Combat --------------------------------------------------------------
  defeated_10_trainers: {
    id: 'defeated_10_trainers',
    name: 'Pitch Practice',
    description: 'Defeat 10 rival trainers.',
    icon: '⚔️',
  },
  defeated_all_trainers: {
    id: 'defeated_all_trainers',
    name: 'Market Dominance',
    description: 'Defeat every named trainer in the region.',
    icon: '👑',
  },

  // --- Exploration ---------------------------------------------------------
  reached_route_1: {
    id: 'reached_route_1',
    name: 'Left the Garage',
    description: 'Set foot on Route 1.',
    icon: '🛤️',
  },
  reached_town: {
    id: 'reached_town',
    name: 'Office Space',
    description: 'Arrive at the first town.',
    icon: '🏘️',
  },
  reached_cave: {
    id: 'reached_cave',
    name: 'Stealth Mode',
    description: 'Enter the deep cave.',
    icon: '🕳️',
  },
  reached_legendary: {
    id: 'reached_legendary',
    name: 'Unicorn Status',
    description: 'Reach the legendary encounter zone.',
    icon: '🦄',
    hidden: true,
  },

  // --- Progression ---------------------------------------------------------
  level_25: {
    id: 'level_25',
    name: 'Mid-Stage',
    description: 'Train a Surggie to level 25.',
    icon: '📊',
  },
  level_50: {
    id: 'level_50',
    name: 'IPO Ready',
    description: 'Train a Surggie to level 50.',
    icon: '💎',
  },
  full_party: {
    id: 'full_party',
    name: 'Founding Team',
    description: 'Carry a full party of 6 Surggies.',
    icon: '🤝',
  },

  // --- Skill / Speedrun ----------------------------------------------------
  no_faints_route_1: {
    id: 'no_faints_route_1',
    name: 'Zero Burn',
    description: 'Clear Route 1 without a single Surggie fainting.',
    icon: '🛡️',
    hidden: true,
  },
  speedrun_route_1: {
    id: 'speedrun_route_1',
    name: 'Blitzscale',
    description: 'Clear Route 1 in under five minutes.',
    icon: '⚡',
    hidden: true,
  },
  million_dollar_team: {
    id: 'million_dollar_team',
    name: 'Million Dollar Team',
    description: 'Reach a combined party level of 50.',
    icon: '💰',
  },

  // --- New achievements (batch 2) -------------------------------------------
  map_explorer: {
    id: 'map_explorer',
    name: 'Map Explorer',
    description: 'Visit your first new area.',
    icon: '🗺️',
  },
  trainer_first_win: {
    id: 'trainer_first_win',
    name: 'Battle Tested',
    description: 'Defeat your first trainer.',
    icon: '🏆',
  },
  caught_five: {
    id: 'caught_five',
    name: 'Collector',
    description: 'Catch 5 Surggies.',
    icon: '🎯',
  },
  plugin_master: {
    id: 'plugin_master',
    name: 'Plugin Master',
    description: 'Equip 5 different plugins across your party.',
    icon: '🔌',
  },
  all_types: {
    id: 'all_types',
    name: 'Type Collector',
    description: 'Catch a Surggie of every type.',
    icon: '🌈',
  },
  battle_hundred: {
    id: 'battle_hundred',
    name: 'Battle-Hardened',
    description: 'Complete 100 battles.',
    icon: '⚔️',
  },
  rich: {
    id: 'rich',
    name: 'Flush with Funding',
    description: 'Accumulate 1000 coins.',
    icon: '💰',
  },
  speedrun: {
    id: 'speedrun',
    name: 'Velocity Hire',
    description: 'Catch a Surggie within 1 minute of game start.',
    icon: '⚡',
    hidden: true,
  },
};

export type AchievementState = {
  /** Unix ms timestamp the achievement was unlocked. Missing key = locked. */
  unlockedAt: Record<AchievementId, number>;
  counters: {
    caughtTotal: number;
    defeatedTrainers: number;
    mapsVisited: string[];
    highestLevel: number;
    /** Total battles completed (any kind). */
    battlesTotal: number;
    /** Total plugins installed across the party. */
    pluginsInstalled: number;
    /** Current coin balance (caller updates via onCoinsUpdate). */
    coins: number;
    /** Set of Surggie types currently owned (for all_types). */
    typesOwned: string[];
    /** Unix ms of the first session start (set once on first onSessionStart). */
    sessionStartMs: number | null;
  };
};

type UnlockHandler = (id: AchievementId) => void;

function emptyState(): AchievementState {
  return {
    unlockedAt: {} as Record<AchievementId, number>,
    counters: {
      caughtTotal: 0,
      defeatedTrainers: 0,
      mapsVisited: [],
      highestLevel: 0,
      battlesTotal: 0,
      pluginsInstalled: 0,
      coins: 0,
      typesOwned: [],
      sessionStartMs: null,
    },
  };
}

export class AchievementTracker {
  private _state: AchievementState;
  private _handlers: Set<UnlockHandler> = new Set();

  constructor(initial?: AchievementState) {
    this._state = initial ? cloneState(initial) : emptyState();
  }

  get state(): Readonly<AchievementState> {
    return this._state;
  }

  // --- Event hooks --------------------------------------------------------

  onCatch(speciesId: string, speciesType?: string): AchievementId[] {
    const unlocked: AchievementId[] = [];
    this._state.counters.caughtTotal += 1;
    const total = this._state.counters.caughtTotal;

    if (total === 1 && this.unlock('first_catch')) unlocked.push('first_catch');
    if (total >= 5 && this.unlock('caught_five')) unlocked.push('caught_five');
    if (total >= 10 && this.unlock('caught_10')) unlocked.push('caught_10');
    if (total >= 25 && this.unlock('caught_25')) unlocked.push('caught_25');
    if (total >= TOTAL_SPECIES && this.unlock('caught_all')) {
      unlocked.push('caught_all');
    }

    // speedrun: first catch within 60 seconds of session start
    if (
      total === 1 &&
      this._state.counters.sessionStartMs !== null &&
      Date.now() - this._state.counters.sessionStartMs <= 60_000
    ) {
      if (this.unlock('speedrun')) unlocked.push('speedrun');
    }

    // all_types: track species types
    if (speciesType && !this._state.counters.typesOwned.includes(speciesType)) {
      this._state.counters.typesOwned.push(speciesType);
    }
    const ALL_TYPES = ['sales', 'tech', 'finance', 'creative', 'ops', 'hr'];
    if (ALL_TYPES.every((t) => this._state.counters.typesOwned.includes(t))) {
      if (this.unlock('all_types')) unlocked.push('all_types');
    }

    // suppress unused-variable warning — speciesId kept for API symmetry
    void speciesId;
    return unlocked;
  }

  onEvolve(_speciesId: string): AchievementId[] {
    const unlocked: AchievementId[] = [];
    if (this.unlock('first_evolution')) unlocked.push('first_evolution');
    return unlocked;
  }

  onTrainerDefeat(_trainerId: string): AchievementId[] {
    const unlocked: AchievementId[] = [];
    this._state.counters.defeatedTrainers += 1;
    const total = this._state.counters.defeatedTrainers;

    if (total === 1) {
      if (this.unlock('first_trainer_win')) unlocked.push('first_trainer_win');
      if (this.unlock('trainer_first_win')) unlocked.push('trainer_first_win');
    }
    if (total >= 10 && this.unlock('defeated_10_trainers')) {
      unlocked.push('defeated_10_trainers');
    }
    if (total >= TOTAL_TRAINERS && this.unlock('defeated_all_trainers')) {
      unlocked.push('defeated_all_trainers');
    }
    return unlocked;
  }

  onMapVisit(mapId: string): AchievementId[] {
    const unlocked: AchievementId[] = [];
    if (!this._state.counters.mapsVisited.includes(mapId)) {
      this._state.counters.mapsVisited.push(mapId);
    }

    // map_explorer: first visit to any non-overworld map
    if (mapId !== 'overworld' && this.unlock('map_explorer')) {
      unlocked.push('map_explorer');
    }

    const mapToAch: Partial<Record<string, AchievementId>> = {
      route_1: 'reached_route_1',
      town: 'reached_town',
      cave: 'reached_cave',
      legendary: 'reached_legendary',
    };
    const ach = mapToAch[mapId];
    if (ach && this.unlock(ach)) unlocked.push(ach);
    return unlocked;
  }

  onLevelUp(newLevel: number): AchievementId[] {
    const unlocked: AchievementId[] = [];
    if (newLevel > this._state.counters.highestLevel) {
      this._state.counters.highestLevel = newLevel;
    }
    if (newLevel >= 25 && this.unlock('level_25')) unlocked.push('level_25');
    if (newLevel >= 50 && this.unlock('level_50')) unlocked.push('level_50');
    return unlocked;
  }

  onSave(): AchievementId[] {
    const unlocked: AchievementId[] = [];
    if (this.unlock('first_save')) unlocked.push('first_save');
    return unlocked;
  }

  /** Call once when a new game session begins (used for speedrun timing). */
  onSessionStart(): void {
    if (this._state.counters.sessionStartMs === null) {
      this._state.counters.sessionStartMs = Date.now();
    }
  }

  /** Call after every completed battle (win or loss). */
  onBattleComplete(): AchievementId[] {
    const unlocked: AchievementId[] = [];
    this._state.counters.battlesTotal += 1;
    if (
      this._state.counters.battlesTotal >= 100 &&
      this.unlock('battle_hundred')
    ) {
      unlocked.push('battle_hundred');
    }
    return unlocked;
  }

  /** Call whenever a plugin is installed. `totalInstalled` is the new cumulative count. */
  onPluginInstall(totalInstalled: number): AchievementId[] {
    const unlocked: AchievementId[] = [];
    if (totalInstalled > this._state.counters.pluginsInstalled) {
      this._state.counters.pluginsInstalled = totalInstalled;
    }
    if (this._state.counters.pluginsInstalled >= 5 && this.unlock('plugin_master')) {
      unlocked.push('plugin_master');
    }
    return unlocked;
  }

  /** Call whenever the player's coin balance changes. */
  onCoinsUpdate(newBalance: number): AchievementId[] {
    const unlocked: AchievementId[] = [];
    this._state.counters.coins = newBalance;
    if (newBalance >= 1000 && this.unlock('rich')) unlocked.push('rich');
    return unlocked;
  }

  /** Call whenever the active party roster changes. `partySize` is 1–6. */
  onPartyUpdate(partySize: number): AchievementId[] {
    const unlocked: AchievementId[] = [];
    if (partySize >= 6 && this.unlock('full_party')) unlocked.push('full_party');
    return unlocked;
  }

  // --- Generic unlock -----------------------------------------------------

  unlock(id: AchievementId): boolean {
    if (this._state.unlockedAt[id]) return false;
    this._state.unlockedAt[id] = Date.now();
    for (const handler of this._handlers) handler(id);
    return true;
  }

  // --- UI helpers ---------------------------------------------------------

  getDisplayList(): Array<{
    achievement: Achievement;
    unlockedAt: number | null;
  }> {
    const all = Object.values(ACHIEVEMENTS);
    return all
      .map((achievement) => ({
        achievement,
        unlockedAt: this._state.unlockedAt[achievement.id] ?? null,
      }))
      .sort((a, b) => {
        // Unlocked first (newest unlock at top), then locked in definition order.
        const aU = a.unlockedAt;
        const bU = b.unlockedAt;
        if (aU !== null && bU !== null) return bU - aU;
        if (aU !== null) return -1;
        if (bU !== null) return 1;
        return 0;
      });
  }

  // --- Subscription -------------------------------------------------------

  onUnlock(handler: UnlockHandler): () => void {
    this._handlers.add(handler);
    return () => {
      this._handlers.delete(handler);
    };
  }

  // --- Save / load --------------------------------------------------------

  toSaveData(): AchievementState {
    return cloneState(this._state);
  }

  loadFromSaveData(data: AchievementState): void {
    this._state = cloneState(data);
  }
}

function cloneState(s: AchievementState): AchievementState {
  return {
    unlockedAt: { ...s.unlockedAt },
    counters: {
      caughtTotal: s.counters.caughtTotal,
      defeatedTrainers: s.counters.defeatedTrainers,
      mapsVisited: [...s.counters.mapsVisited],
      highestLevel: s.counters.highestLevel,
      battlesTotal: s.counters.battlesTotal,
      pluginsInstalled: s.counters.pluginsInstalled,
      coins: s.counters.coins,
      typesOwned: [...s.counters.typesOwned],
      sessionStartMs: s.counters.sessionStartMs,
    },
  };
}

/** Process-wide singleton — game systems should import and reuse this. */
export const achievementTracker: AchievementTracker = new AchievementTracker();
